import { Component, Inject, OnDestroy, NgZone } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

export interface ImageModalData {
  imageUrl: string;
}

@Component({
  selector: 'app-image-modal',
  template: `
    <div class="image-modal-container">
      <div class="button-controls">
        <button mat-icon-button class="control-button" (click)="zoomIn()" matTooltip="Zoom In">
          <mat-icon>zoom_in</mat-icon>
        </button>
        <button mat-icon-button class="control-button" (click)="zoomOut()" matTooltip="Zoom Out">
          <mat-icon>zoom_out</mat-icon>
        </button>
        <button mat-icon-button class="control-button" (click)="onClose()" matTooltip="Close">
          <mat-icon>close</mat-icon>
        </button>
      </div>
      <div class="image-container" 
           (mousedown)="startDrag($event)"
           [class.draggable]="isDraggable">
        <img [src]="data.imageUrl" 
             alt="Full size image" 
             class="modal-image"
             [style.transform]="getImageTransform()"
             [style.cursor]="getCursor()"
             (wheel)="handleWheel($event)">
      </div>
    </div>
  `,
  styles: [`
    .image-modal-container {
      position: relative;
      height: 100vh;
      background: rgba(0, 0, 0, 0.9);
      overflow: hidden;
    }

    .button-controls {
      position: absolute;
      top: 10px;
      right: 10px;
      z-index: 1000;
      display: flex;
      gap: 8px;
    }

    .control-button {
      color: white;
      background: rgba(255, 255, 255, 0.1);
      &:hover {
        background: rgba(255, 255, 255, 0.2);
      }
    }

    .image-container {
      height: 100%;
      width: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      overflow: auto;
      user-select: none;
    }

    .draggable {
      cursor: grab;
      &:active {
        cursor: grabbing;
      }
    }

    .modal-image {
      max-height: 95vh;
      max-width: 95vw;
      object-fit: contain;
      transform-origin: center center;
      transition: transform 0.2s ease-out;
      will-change: transform;
      pointer-events: none;
    }
  `]
})
export class ImageModalComponent implements OnDestroy {
  zoomLevel: number = 1;
  readonly ZOOM_STEP: number = 0.2;
  readonly MAX_ZOOM: number = 3;
  readonly MIN_ZOOM: number = 0.5;

  // Drag functionality
  isDragging: boolean = false;
  startX: number = 0;
  startY: number = 0;
  translateX: number = 0;
  translateY: number = 0;
  lastTranslateX: number = 0;
  lastTranslateY: number = 0;

  private mouseMoveListener: (e: MouseEvent) => void;
  private mouseUpListener: (e: MouseEvent) => void;

  constructor(
    public dialogRef: MatDialogRef<ImageModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ImageModalData,
    private ngZone: NgZone
  ) {
    // Close dialog on escape key
    this.dialogRef.keydownEvents().subscribe(event => {
      if (event.key === 'Escape') {
        this.onClose();
      }
    });

    // Initialize listeners
    this.mouseMoveListener = (e: MouseEvent) => this.ngZone.run(() => this.drag(e));
    this.mouseUpListener = (e: MouseEvent) => this.ngZone.run(() => this.stopDrag());
  }

  ngOnDestroy(): void {
    // Clean up listeners
    this.removeGlobalListeners();
  }

  get isDraggable(): boolean {
    return this.zoomLevel > 1;
  }

  getCursor(): string {
    if (!this.isDraggable) return 'default';
    return this.isDragging ? 'grabbing' : 'grab';
  }

  getImageTransform(): string {
    return `scale(${this.zoomLevel}) translate(${this.translateX}px, ${this.translateY}px)`;
  }

  startDrag(event: MouseEvent): void {
    if (!this.isDraggable) return;
    
    event.preventDefault();
    this.isDragging = true;
    this.startX = event.clientX - this.lastTranslateX;
    this.startY = event.clientY - this.lastTranslateY;

    // Add global listeners
    document.addEventListener('mousemove', this.mouseMoveListener);
    document.addEventListener('mouseup', this.mouseUpListener);
  }

  private removeGlobalListeners(): void {
    document.removeEventListener('mousemove', this.mouseMoveListener);
    document.removeEventListener('mouseup', this.mouseUpListener);
  }

  drag(event: MouseEvent): void {
    if (!this.isDragging) return;

    event.preventDefault();
    this.translateX = (event.clientX - this.startX) / this.zoomLevel;
    this.translateY = (event.clientY - this.startY) / this.zoomLevel;
  }

  stopDrag(): void {
    if (!this.isDragging) return;
    
    this.lastTranslateX = this.translateX * this.zoomLevel;
    this.lastTranslateY = this.translateY * this.zoomLevel;
    this.isDragging = false;

    // Remove global listeners
    this.removeGlobalListeners();
  }

  handleWheel(event: WheelEvent): void {
    event.preventDefault();
    if (event.deltaY < 0) {
      this.zoomIn();
    } else {
      this.zoomOut();
    }
  }

  zoomIn(): void {
    if (this.zoomLevel < this.MAX_ZOOM) {
      this.zoomLevel = Math.min(this.MAX_ZOOM, this.zoomLevel + this.ZOOM_STEP);
    }
  }

  zoomOut(): void {
    if (this.zoomLevel > this.MIN_ZOOM) {
      this.zoomLevel = Math.max(this.MIN_ZOOM, this.zoomLevel - this.ZOOM_STEP);
      // Reset position when zooming out to 1 or below
      if (this.zoomLevel <= 1) {
        this.resetPosition();
      }
    }
  }

  resetPosition(): void {
    this.translateX = 0;
    this.translateY = 0;
    this.lastTranslateX = 0;
    this.lastTranslateY = 0;
  }

  onClose(): void {
    this.dialogRef.close();
  }
} 